import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, Navigate, useNavigate, useParams } from "react-router";
import {
  ArrowLeft,
  CalendarDays,
  Check,
  CreditCard,
  FileText,
  LayoutGrid,
  Loader2,
  Megaphone,
  PencilLine,
  Save,
  Sparkles,
  Target,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { normalizeCampaignBrief } from "@runcheetah/shared";
import { toast } from "sonner";

import type { CampaignSlotCardData } from "@/components/campaign/campaign-slot-card";
import {
  GeneratedPostReviewCard,
  GeneratedPostVisualSpinner,
} from "@/components/campaign/generated-post-review-card";
import { SocialPostPreviewCard } from "@/components/campaign/social-post-preview-card";
import { RunConfirmationDialog } from "@/components/runs/run-confirmation-dialog";
import { Badge } from "@runcheetah/ui";
import { Button } from "@runcheetah/ui";
import { Card, CardContent } from "@runcheetah/ui";
import { Input } from "@runcheetah/ui";
import { Textarea } from "@runcheetah/ui";
import { Skeleton, SkeletonTextBlock } from "@/components/common";
import {
  areCampaignEditorStatesEqual,
  buildCampaignScheduleInput,
  buildCampaignUpdateInput,
  createCampaignEditorState,
  type CampaignEditorState,
} from "@/lib/campaign-editor";
import { normalizeCampaignScheduledPosts } from "@/lib/campaign-posts";
import { useAssetCreationGate } from "@/hooks/use-asset-creation-gate";
import { buildBrandPath, getUnavailableBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import { buildFocusedPostEditorPath } from "@/lib/focused-post-editor";
import { formatInstagramProfileLabel } from "@/lib/social-post-chrome";
import {
  resolveScheduledPostPreview,
  type ScheduledPostPreviewLogoAsset,
} from "@/lib/scheduled-post-preview";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { hasActiveInstagramPublishNow } from "@/lib/instagram-publication";

import {
  getCampaignGeneratedContentCounts,
  getCampaignScheduleSummary,
  getNextCampaignDetailPost,
  parseCampaignAudienceSegments,
  serializeCampaignAudienceSegments,
} from "./campaign-detail-state";

const CAMPAIGN_STATUS_COLORS: Record<string, string> = {
  draft: "border-foreground/15 bg-foreground text-background",
  pending_review: "border-peach/30 bg-peach text-peach-foreground",
  approved: "border-sage/30 bg-sage text-sage-foreground",
  active: "border-sage/30 bg-sage text-sage-foreground",
  paused: "border-peach/30 bg-peach text-peach-foreground",
  completed: "border-foreground/15 bg-foreground/85 text-background",
};

type GeneratedContentFilter = "all" | "pending" | "approved" | "denied";
type InlineCampaignField = "summary" | "callToActions" | "audience" | "schedule";

export default function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const intl = useIntl();
  const [generatedContentFilter, setGeneratedContentFilter] =
    useState<GeneratedContentFilter>("all");
  const [activeInlineField, setActiveInlineField] = useState<InlineCampaignField | null>(null);
  const [audienceDraft, setAudienceDraft] = useState("");
  const [editorState, setEditorState] = useState<CampaignEditorState | null>(null);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [previewRegenerationNotice, setPreviewRegenerationNotice] = useState(false);
  const [pendingSaveConfirmation, setPendingSaveConfirmation] = useState(false);
  const [pendingPreviewRegenerationConfirmation, setPendingPreviewRegenerationConfirmation] =
    useState(false);
  const [pendingApprovalConfirmation, setPendingApprovalConfirmation] = useState(false);
  const [pendingRetryScheduledPostId, setPendingRetryScheduledPostId] = useState<string | null>(
    null,
  );
  const { brand, brandQuery } = useRouteBrand();
  const assetCreationGate = useAssetCreationGate(brand?.id);

  const campaignQuery = trpc.campaign.get.useQuery(
    { brandId: brand?.id ?? "", id: id ?? "" },
    {
      enabled: !!brand?.id && !!id,
      refetchOnWindowFocus: true,
      refetchInterval: (query) =>
        hasActiveInstagramPublishNow(query.state.data?.scheduledPosts) ? 2_000 : false,
    },
  );
  const logosQuery = trpc.brand.listLogos.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), staleTime: 0 },
  );
  const campaign = campaignQuery.data ?? null;
  const utils = trpc.useUtils();
  const approveCampaign = trpc.campaign.approve.useMutation({
    onSuccess: async () => {
      if (!brand?.id || !id) {
        return;
      }
      await utils.campaign.get.invalidate({ brandId: brand.id, id });
      await utils.campaign.list.invalidate({ brandId: brand.id });
      await utils.brand.getBillingOverview.invalidate({ brandId: brand.id });
      setPendingApprovalConfirmation(false);
    },
  });
  const rejectCampaign = trpc.campaign.reject.useMutation({
    onSuccess: async (_deletedCampaign, variables) => {
      await utils.campaign.get.invalidate({ brandId: variables.brandId, id: variables.id });
      await utils.campaign.list.invalidate({ brandId: variables.brandId });
      navigate(buildBrandPath(variables.brandId, "/campaigns"), { replace: true });
    },
  });
  const updateCampaign = trpc.campaign.update.useMutation();
  const setCampaignSchedule = trpc.campaign.setSchedule.useMutation();
  const regeneratePreview = trpc.campaign.regeneratePreview.useMutation({
    onSuccess: async () => {
      if (!brand?.id || !id) {
        return;
      }

      await utils.campaign.get.invalidate({ brandId: brand.id, id });
      await utils.campaign.list.invalidate({ brandId: brand.id });
      await utils.brand.getBillingOverview.invalidate({ brandId: brand.id });
      setPreviewRegenerationNotice(true);
      setPendingPreviewRegenerationConfirmation(false);
      setGeneratedContentFilter("all");
    },
  });
  const publishNowInstagram = trpc.campaign.publishNowInstagram.useMutation({
    onSuccess: async () => {
      if (!brand?.id || !id) {
        return;
      }

      await utils.campaign.get.invalidate({ brandId: brand.id, id });
      await utils.campaign.list.invalidate({ brandId: brand.id });
      await utils.brand.getBillingOverview.invalidate({ brandId: brand.id });
      setPendingRetryScheduledPostId(null);
      toast.success(
        intl.formatMessage({
          id: "campaignDetail.publishNowStarted",
          defaultMessage: "Instagram publishing started.",
        }),
      );
    },
    onError: (error) => {
      toast.error(
        intl.formatMessage(
          {
            id: "campaignDetail.publishNowFailed",
            defaultMessage: "Could not publish to Instagram: {message}",
          },
          { message: error.message },
        ),
      );
    },
  });
  const retryScheduledPostGeneration = trpc.campaign.retryScheduledPostGeneration.useMutation({
    onSuccess: async () => {
      if (!brand?.id || !id) {
        return;
      }

      await utils.campaign.get.invalidate({ brandId: brand.id, id });
      await utils.campaign.list.invalidate({ brandId: brand.id });
    },
  });
  const approveScheduledPost = trpc.calendar.approve.useMutation({
    onSuccess: async () => {
      if (!brand?.id || !id) {
        return;
      }

      await utils.campaign.get.invalidate({ brandId: brand.id, id });
      await utils.campaign.list.invalidate({ brandId: brand.id });
      await utils.calendar.list.invalidate();
    },
  });
  const rejectScheduledPost = trpc.calendar.reject.useMutation({
    onSuccess: async () => {
      if (!brand?.id || !id) {
        return;
      }

      await utils.campaign.get.invalidate({ brandId: brand.id, id });
      await utils.campaign.list.invalidate({ brandId: brand.id });
      await utils.calendar.list.invalidate();
    },
  });

  const campaignBrief = useMemo(
    () =>
      normalizeCampaignBrief({
        briefJson: campaign && "brief" in campaign ? campaign.brief : undefined,
        goals: campaign && "goals" in campaign ? campaign.goals : undefined,
        audience: campaign && "audience" in campaign ? campaign.audience : undefined,
      }),
    [campaign],
  );

  const plannedPosts = useMemo<CampaignSlotCardData[]>(
    () => normalizeCampaignScheduledPosts(campaign?.scheduledPosts),
    [campaign?.scheduledPosts],
  );
  const logoAssets = useMemo(
    () =>
      (logosQuery.data?.logos ?? []).map((logo) => ({
        objectKey: logo.objectKey,
        signedUrl: logo.signedUrl,
        isPrimary: logo.isPrimary,
      })),
    [logosQuery.data?.logos],
  );
  const generatedPosts = useMemo(
    () => plannedPosts.filter((post) => post.status === "generated"),
    [plannedPosts],
  );
  const pendingProductionPosts = useMemo(
    () => plannedPosts.filter((post) => post.status !== "generated"),
    [plannedPosts],
  );
  const visibleGeneratedPosts = useMemo(
    () =>
      generatedPosts.filter((post) => {
        if (generatedContentFilter === "all") {
          return true;
        }

        if (generatedContentFilter === "denied") {
          return post.approvalStatus === "rejected";
        }

        return post.approvalStatus === generatedContentFilter;
      }),
    [generatedContentFilter, generatedPosts],
  );
  const generatedContentCounts = useMemo(
    () => getCampaignGeneratedContentCounts(plannedPosts),
    [plannedPosts],
  );
  const hasInFlightPreviewRegenerationPosts = useMemo(
    () => plannedPosts.some((post) => isPostGenerationInFlight(post)),
    [plannedPosts],
  );
  const nextPlannedPost = useMemo(() => getNextCampaignDetailPost(plannedPosts), [plannedPosts]);
  const profileLabel = useMemo(
    () => formatInstagramProfileLabel(brand?.sourceDomain ?? ""),
    [brand?.sourceDomain],
  );
  const campaignEditorState = useMemo(() => {
    if (!campaign) {
      return null;
    }

    return createCampaignEditorState({
      name: campaign.name,
      startDate: String(campaign.startDate),
      endDate: String(campaign.endDate),
      brief: "brief" in campaign ? campaign.brief : undefined,
      goals: "goals" in campaign ? campaign.goals : undefined,
      audience: "audience" in campaign ? campaign.audience : undefined,
      scheduledPosts: (campaign.scheduledPosts ?? []).map((post) => ({
        id: post.id,
        scheduledFor: String(post.scheduledFor),
        slot: "slot" in post ? post.slot : undefined,
      })),
    });
  }, [campaign]);

  useEffect(() => {
    if (!campaignEditorState) {
      return;
    }

    setEditorState(campaignEditorState);
    setEditorError(null);
    setPreviewRegenerationNotice(false);
  }, [campaignEditorState]);

  useEffect(() => {
    if (!brand?.id || !id || !hasInFlightPreviewRegenerationPosts) {
      return;
    }

    const interval = window.setInterval(() => {
      void campaignQuery.refetch();
    }, 5000);

    return () => window.clearInterval(interval);
  }, [brand?.id, campaignQuery, hasInFlightPreviewRegenerationPosts, id]);

  const startDate = new Date(String(campaign?.startDate ?? new Date().toISOString()));
  const endDate = new Date(String(campaign?.endDate ?? new Date().toISOString()));
  const shortDateFmt = useMemo(
    () => new Intl.DateTimeFormat(intl.locale, { month: "short", day: "numeric" }),
    [intl.locale],
  );
  const longDateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(intl.locale, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }),
    [intl.locale],
  );
  const scheduleSummary = campaign
    ? getCampaignScheduleSummary({
        startDate: campaign.startDate,
        endDate: campaign.endDate,
        postCount: plannedPosts.length,
        formatDate: (date) => shortDateFmt.format(date),
      })
    : "";
  const generatedContentTabs = getGeneratedContentTabs(generatedContentCounts, intl);
  const saveEditorPending = updateCampaign.isPending || setCampaignSchedule.isPending;
  const approvalQuoteQuery = trpc.campaign.quoteApprove.useQuery(
    {
      brandId: brand?.id ?? "00000000-0000-0000-0000-000000000000",
      id: campaign?.id ?? "00000000-0000-0000-0000-000000000000",
    },
    { enabled: Boolean(pendingApprovalConfirmation && brand?.id && campaign?.id), retry: false },
  );
  const previewRegenerationQuoteQuery = trpc.campaign.quotePreviewRegeneration.useQuery(
    {
      brandId: brand?.id ?? "00000000-0000-0000-0000-000000000000",
      id: campaign?.id ?? "00000000-0000-0000-0000-000000000000",
    },
    {
      enabled: Boolean(
        (pendingSaveConfirmation || pendingPreviewRegenerationConfirmation) &&
        brand?.id &&
        campaign?.id,
      ),
      retry: false,
    },
  );
  const retryGenerationQuoteQuery = trpc.campaign.quoteRetryScheduledPostGeneration.useQuery(
    {
      brandId: brand?.id ?? "00000000-0000-0000-0000-000000000000",
      campaignId: campaign?.id ?? "00000000-0000-0000-0000-000000000000",
      scheduledPostId: pendingRetryScheduledPostId ?? "00000000-0000-0000-0000-000000000000",
    },
    {
      enabled: Boolean(pendingRetryScheduledPostId && brand?.id && campaign?.id),
      retry: false,
    },
  );
  const hasUnsavedEditorChanges = useMemo(
    () =>
      Boolean(
        editorState &&
        campaignEditorState &&
        !areCampaignEditorStatesEqual(editorState, campaignEditorState),
      ),
    [campaignEditorState, editorState],
  );
  const approveDisabled = approveCampaign.isPending || saveEditorPending || hasUnsavedEditorChanges;
  const hasUsedPreviewRegeneration = (campaign?.previewRegenerationCount ?? 0) > 0;
  const regeneratePreviewDisabled =
    campaign?.status !== "pending_review" ||
    hasUsedPreviewRegeneration ||
    saveEditorPending ||
    regeneratePreview.isPending ||
    hasUnsavedEditorChanges;
  const editableAudienceText = editorState
    ? serializeCampaignAudienceSegments(editorState.audienceSegments)
    : serializeCampaignAudienceSegments(campaignBrief.audienceSegments);
  const editableCallToActionsText =
    editorState?.callToActions ?? campaignBrief.callToActions.join("\n");
  const editableSummaryText = editorState?.summary ?? campaignBrief.summary;

  const updateEditorState = (updater: (current: CampaignEditorState) => CampaignEditorState) => {
    setEditorState((current) => (current ? updater(current) : current));
  };

  const resetEditorState = () => {
    if (!campaignEditorState) {
      return;
    }

    setEditorState(campaignEditorState);
    setEditorError(null);
    setActiveInlineField(null);
    setAudienceDraft("");
  };

  const closeInlineField = () => setActiveInlineField(null);

  const saveCampaignEditor = async () => {
    if (!brand?.id || !campaign || !editorState) {
      return;
    }

    setEditorError(null);

    try {
      await updateCampaign.mutateAsync(
        buildCampaignUpdateInput({
          brandId: brand.id,
          campaignId: campaign.id,
          state: editorState,
        }),
      );
      await setCampaignSchedule.mutateAsync(
        buildCampaignScheduleInput({
          brandId: brand.id,
          campaignId: campaign.id,
          state: editorState,
        }),
      );
      await utils.campaign.get.invalidate({ brandId: brand.id, id: campaign.id });
      await utils.campaign.list.invalidate({ brandId: brand.id });
      setPreviewRegenerationNotice(true);
      setPendingSaveConfirmation(false);
      setGeneratedContentFilter("all");
      setActiveInlineField(null);
      setAudienceDraft("");
    } catch (error) {
      setEditorError(
        error instanceof Error
          ? error.message
          : intl.formatMessage({
              id: "campaignDetail.saveErrorFallback",
              defaultMessage: "Could not save campaign edits.",
            }),
      );
    }
  };

  const confirmCampaignApproval = () => {
    if (!brand?.id || !campaign?.id) {
      return;
    }

    approveCampaign.mutate({ brandId: brand.id, id: campaign.id });
  };

  const confirmRetryScheduledPostGeneration = () => {
    if (!brand?.id || !campaign?.id || !pendingRetryScheduledPostId) {
      return;
    }

    retryScheduledPostGeneration.mutate({
      brandId: brand.id,
      campaignId: campaign.id,
      scheduledPostId: pendingRetryScheduledPostId,
    });
  };

  const confirmPreviewRegeneration = () => {
    if (!brand?.id || !campaign?.id) {
      return;
    }

    regeneratePreview.mutate({ brandId: brand.id, id: campaign.id });
  };

  if (brandQuery.isPending || campaignQuery.isPending) {
    return (
      <div className="min-h-full w-full bg-background" aria-hidden="true">
        <div className="mx-auto w-full max-w-[92rem] px-5 pb-20 pt-6 lg:px-10 lg:pt-8">
          <div className="grid gap-8 xl:grid-cols-[minmax(0,1.75fr)_20rem] xl:items-start">
            <div className="space-y-8">
              <section className="rounded-[1.75rem] border border-border/70 bg-card px-6 py-6 shadow-soft sm:px-7 sm:py-7">
                <Skeleton className="h-9 w-32" />
                <div className="mt-5 flex flex-wrap gap-2.5">
                  <Skeleton className="h-6 w-28 rounded-full" />
                  <Skeleton className="h-6 w-44 rounded-full" />
                </div>
                <Skeleton className="mt-6 h-12 w-3/4" />
                <SkeletonTextBlock className="mt-4 max-w-4xl" />
                <div className="mt-7 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <Skeleton key={index} className="h-20" />
                  ))}
                </div>
                <Skeleton className="mt-6 aspect-[16/7] rounded-[1.5rem]" />
              </section>
              <section className="rounded-[1.75rem] border border-border/70 bg-card px-6 py-6 shadow-soft sm:px-7">
                <Skeleton className="h-5 w-44" />
                <div className="mt-5 space-y-5 border-t pt-5">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <SkeletonTextBlock key={index} />
                  ))}
                </div>
              </section>
              <section className="rounded-[1.75rem] border border-border/70 bg-card px-6 py-6 shadow-soft sm:px-7">
                <div className="flex justify-between gap-4">
                  <SkeletonTextBlock className="w-full max-w-lg" />
                  <Skeleton className="hidden h-10 w-64 md:block" />
                </div>
                <div className="mt-6 gap-6 md:columns-2">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="mb-6 break-inside-avoid rounded-[1.4rem] border">
                      <Skeleton className="aspect-[4/5] rounded-b-none" />
                      <div className="space-y-4 p-5">
                        <SkeletonTextBlock />
                        <Skeleton className="h-10 w-full" />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
            <aside className="space-y-5">
              <Skeleton className="h-64 rounded-[1.75rem]" />
              <Skeleton className="h-64 rounded-[1.75rem]" />
            </aside>
          </div>
        </div>
      </div>
    );
  }

  if (!brand || brand.status !== "scanned") {
    return <Navigate to={getUnavailableBrandPath(brand?.id)} replace />;
  }

  if (!campaign) {
    return <Navigate to="/app" replace />;
  }

  return (
    <div className="min-h-full w-full bg-background">
      <div className="mx-auto w-full max-w-[92rem] px-4 pb-32 pt-5 sm:px-5 lg:px-8 lg:pt-6">
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.75fr)_19rem] xl:items-start">
          <div className="flex flex-col gap-6">
            <header className="order-1 border-b border-border/70 pb-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-4">
                  <Button asChild variant="ghost" size="sm" className="-ml-2 w-fit rounded-xl">
                    <Link to={buildBrandPath(brand.id, "/campaigns")}>
                      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                      <FormattedMessage id="campaignDetail.back" defaultMessage="Campaigns" />
                    </Link>
                  </Button>

                  <div className="flex flex-wrap items-center gap-2.5">
                    <Badge
                      shape="pill"
                      variant="outline"
                      className={cn(
                        "text-[11px] uppercase tracking-[0.18em]",
                        CAMPAIGN_STATUS_COLORS[campaign.status],
                      )}
                    >
                      {intl.formatMessage({
                        id: `campaignDetail.campaignStatus.${campaign.status}`,
                        defaultMessage: getDefaultCampaignStatusLabel(campaign.status),
                      })}
                    </Badge>
                    <span className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                      {shortDateFmt.format(startDate)} - {shortDateFmt.format(endDate)}
                    </span>
                    {nextPlannedPost ? (
                      <span className="text-xs uppercase tracking-[0.14em] text-foreground/58">
                        {intl.formatMessage(
                          {
                            id: "campaignDetail.nextPlannedPost",
                            defaultMessage: "Next planned post {date}",
                          },
                          { date: longDateFmt.format(new Date(nextPlannedPost.scheduledFor)) },
                        )}
                      </span>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <h1 className="max-w-4xl text-balance text-2xl font-semibold tracking-tight text-foreground">
                      {campaign.name}
                    </h1>
                  </div>
                </div>
              </div>
            </header>

            <section className="order-2 rounded-3xl border border-border/70 bg-card px-5 py-2 shadow-soft sm:px-6">
              <div className="flex items-center gap-2 px-1 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                <Target className="h-4 w-4" aria-hidden="true" />
                <FormattedMessage
                  id="campaignDetail.detailsTitle"
                  defaultMessage="Campaign Details"
                />
              </div>

              {campaign.status === "pending_review" ? (
                <div className="mb-4 rounded-2xl border border-peach/35 bg-peach/12 px-4 py-3 text-sm leading-6 text-foreground">
                  <div className="flex min-w-0 gap-3">
                    <PencilLine
                      className="mt-1 h-4 w-4 shrink-0 text-foreground/58"
                      aria-hidden="true"
                    />
                    <div>
                      <p className="font-semibold tracking-tight">
                        <FormattedMessage
                          id="campaignDetail.pendingEditHintTitle"
                          defaultMessage="Edit the campaign details before regenerating."
                        />
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              <section className="divide-y divide-border/70 border-t border-border/70">
                <InlineCampaignDetailRow
                  icon={Target}
                  label={intl.formatMessage({
                    id: "campaignDetail.themeTitle",
                    defaultMessage: "Theme",
                  })}
                  editing={activeInlineField === "summary"}
                  onEdit={() => setActiveInlineField("summary")}
                >
                  {activeInlineField === "summary" ? (
                    <Textarea
                      value={editableSummaryText}
                      onChange={(event) =>
                        updateEditorState((current) => ({
                          ...current,
                          summary: event.target.value,
                        }))
                      }
                      onBlur={closeInlineField}
                      className="min-h-24 text-sm leading-6"
                      autoFocus
                    />
                  ) : (
                    <p className="text-[15px] font-medium leading-6 text-foreground">
                      {editableSummaryText}
                    </p>
                  )}
                </InlineCampaignDetailRow>

                <InlineCampaignDetailRow
                  icon={Megaphone}
                  label={intl.formatMessage({
                    id: "campaignDetail.callToActionTitle",
                    defaultMessage: "Call to Action",
                  })}
                  editing={activeInlineField === "callToActions"}
                  onEdit={() => setActiveInlineField("callToActions")}
                >
                  {activeInlineField === "callToActions" ? (
                    <Textarea
                      value={editableCallToActionsText}
                      onChange={(event) =>
                        updateEditorState((current) => ({
                          ...current,
                          callToActions: event.target.value,
                        }))
                      }
                      onBlur={closeInlineField}
                      className="min-h-20 text-sm leading-6"
                      autoFocus
                    />
                  ) : (
                    <p className="text-[15px] font-medium leading-6 text-foreground">
                      {editableCallToActionsText.trim() ||
                        intl.formatMessage({
                          id: "campaignDetail.ctaFallback",
                          defaultMessage: "No CTA direction has been defined yet.",
                        })}
                    </p>
                  )}
                </InlineCampaignDetailRow>

                <InlineCampaignDetailRow
                  icon={Users}
                  label={intl.formatMessage({
                    id: "campaignDetail.audienceTitle",
                    defaultMessage: "Audience",
                  })}
                  editing={activeInlineField === "audience"}
                  onEdit={() => {
                    setAudienceDraft(editableAudienceText);
                    setActiveInlineField("audience");
                  }}
                >
                  {activeInlineField === "audience" ? (
                    <Textarea
                      value={audienceDraft}
                      onChange={(event) => {
                        setAudienceDraft(event.target.value);
                        updateEditorState((current) => ({
                          ...current,
                          audienceSegments: parseCampaignAudienceSegments(event.target.value).map(
                            (segment) => ({
                              label: segment.label,
                              description: segment.description ?? "",
                            }),
                          ),
                        }));
                      }}
                      onBlur={closeInlineField}
                      className="min-h-28 text-sm leading-6"
                      placeholder={intl.formatMessage({
                        id: "campaignDetail.audiencePlaceholder",
                        defaultMessage: "Segment - Description",
                      })}
                      autoFocus
                    />
                  ) : (
                    <p className="whitespace-pre-wrap text-[15px] font-medium leading-6 text-foreground">
                      {editableAudienceText ||
                        intl.formatMessage({
                          id: "campaignDetail.audienceFallback",
                          defaultMessage:
                            "Audience segments will appear here once the campaign brief is finalized.",
                        })}
                    </p>
                  )}
                </InlineCampaignDetailRow>
              </section>

              {campaign.status === "pending_review" ? (
                <div className="flex justify-end border-t border-border/70 px-1 py-4">
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full rounded-xl sm:w-fit"
                    onClick={() =>
                      hasUnsavedEditorChanges
                        ? setPendingSaveConfirmation(true)
                        : setPendingPreviewRegenerationConfirmation(true)
                    }
                    disabled={
                      hasUsedPreviewRegeneration ||
                      saveEditorPending ||
                      regeneratePreview.isPending ||
                      (!hasUnsavedEditorChanges && regeneratePreviewDisabled)
                    }
                  >
                    {saveEditorPending || regeneratePreview.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <Sparkles className="h-4 w-4" aria-hidden="true" />
                    )}
                    <FormattedMessage
                      id="campaignDetail.regeneratePreview"
                      defaultMessage="Regenerate preview"
                    />
                  </Button>
                </div>
              ) : null}
            </section>

            <section className="order-3 space-y-5 rounded-3xl border border-border/70 bg-card px-5 py-5 shadow-soft sm:px-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div className="space-y-1">
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                    <LayoutGrid className="h-5 w-5 text-foreground/55" aria-hidden="true" />
                    <FormattedMessage
                      id="campaignDetail.generatedContentTitle"
                      defaultMessage="Generated Content"
                    />
                  </h2>
                  <p className="mt-1 max-w-3xl text-sm leading-6 text-foreground/68">
                    <FormattedMessage
                      id="campaignDetail.generatedContentSubtitle"
                      defaultMessage="Review generated posts, approve or deny them, and open the post workspace for edits."
                    />
                  </p>
                  <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                    <FormattedMessage
                      id="campaignDetail.generatedContentOperationalHint"
                      defaultMessage="Review queue"
                    />
                  </p>
                </div>
                <div className="flex w-full flex-col gap-3 lg:w-fit lg:items-end">
                  <div className="grid w-full grid-cols-2 gap-2 md:w-fit md:grid-cols-4">
                    {generatedContentTabs.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setGeneratedContentFilter(tab.id)}
                        aria-pressed={generatedContentFilter === tab.id}
                        className={cn(
                          "min-h-10 min-w-0 whitespace-nowrap rounded-2xl border border-border/70 bg-background px-2.5 text-[11px] font-medium text-muted-foreground transition hover:border-foreground/20 hover:text-foreground md:px-3",
                          generatedContentFilter === tab.id &&
                            "border-foreground/15 bg-foreground text-background shadow-soft hover:text-background",
                        )}
                      >
                        {tab.label} <span className="opacity-75">{tab.count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {plannedPosts.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center text-sm text-muted-foreground">
                    <FormattedMessage
                      id="campaignDetail.noPosts"
                      defaultMessage="No planned posts yet."
                    />
                  </CardContent>
                </Card>
              ) : visibleGeneratedPosts.length === 0 && generatedContentFilter !== "all" ? (
                <Card>
                  <CardContent className="py-12 text-center text-sm text-muted-foreground">
                    <FormattedMessage
                      id="campaignDetail.noGeneratedPostsForFilter"
                      defaultMessage="No generated posts match this filter yet."
                    />
                  </CardContent>
                </Card>
              ) : (
                <div className="gap-6 md:columns-2 [&>*]:break-inside-avoid">
                  {visibleGeneratedPosts.map((post) => (
                    <div key={post.id} className="mb-6">
                      <GeneratedPostReviewCard
                        post={post}
                        profileLabel={profileLabel}
                        timeLabel={longDateFmt.format(new Date(post.scheduledFor))}
                        logoAssets={logoAssets}
                        editPath={
                          campaign.status === "pending_review"
                            ? null
                            : buildFocusedPostEditorPath({
                                brandId: brand.id,
                                owner: { type: "campaign", id: campaign.id },
                                postId: post.id,
                              })
                        }
                        onApprove={() =>
                          approveScheduledPost.mutate({
                            brandId: brand.id,
                            scheduledPostId: post.id,
                          })
                        }
                        approvePending={
                          approveScheduledPost.isPending &&
                          approveScheduledPost.variables?.scheduledPostId === post.id
                        }
                        onReject={() =>
                          rejectScheduledPost.mutate({
                            brandId: brand.id,
                            scheduledPostId: post.id,
                          })
                        }
                        rejectPending={
                          rejectScheduledPost.isPending &&
                          rejectScheduledPost.variables?.scheduledPostId === post.id
                        }
                        onPublishNow={() =>
                          publishNowInstagram.mutate({
                            brandId: brand.id,
                            campaignId: campaign.id,
                            scheduledPostId: post.id,
                          })
                        }
                        publishNowPending={
                          publishNowInstagram.isPending &&
                          publishNowInstagram.variables?.scheduledPostId === post.id
                        }
                      />
                    </div>
                  ))}

                  {generatedContentFilter === "all"
                    ? pendingProductionPosts.map((post) => (
                        <div key={post.id} className="mb-6">
                          <CampaignQueuedPostCard
                            post={post}
                            campaignStatus={campaign.status}
                            profileLabel={profileLabel}
                            logoAssets={logoAssets}
                            timeLabel={longDateFmt.format(new Date(post.scheduledFor))}
                            onRetryGeneration={() => setPendingRetryScheduledPostId(post.id)}
                            retryGenerationPending={
                              retryScheduledPostGeneration.isPending &&
                              retryScheduledPostGeneration.variables?.scheduledPostId === post.id
                            }
                          />
                        </div>
                      ))
                    : null}
                </div>
              )}
            </section>
          </div>

          <aside className="space-y-5 xl:sticky xl:top-6 xl:max-h-[calc(100vh-9rem)] xl:overflow-y-auto xl:pr-1">
            <Card className="overflow-hidden rounded-3xl border-border/70 shadow-none">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-lg font-semibold text-foreground">
                  <CalendarDays className="h-5 w-5 text-foreground/55" aria-hidden="true" />
                  <FormattedMessage
                    id="campaignDetail.scheduleAccountsTitle"
                    defaultMessage="Schedule"
                  />
                </div>
                <div className="mt-5 divide-y divide-border/70 border-y border-border/70">
                  <InlineCampaignDetailRow
                    icon={CalendarDays}
                    label={intl.formatMessage({
                      id: "campaignDetail.scheduleTitle",
                      defaultMessage: "Schedule",
                    })}
                    editing={activeInlineField === "schedule"}
                    onEdit={() => setActiveInlineField("schedule")}
                    compact={true}
                  >
                    {activeInlineField === "schedule" && editorState ? (
                      <div
                        className="grid gap-3 sm:grid-cols-2"
                        onBlur={(event) => {
                          const nextFocus = event.relatedTarget;
                          if (
                            nextFocus instanceof Node &&
                            event.currentTarget.contains(nextFocus)
                          ) {
                            return;
                          }

                          closeInlineField();
                        }}
                      >
                        <Input
                          type="datetime-local"
                          value={editorState.startDate}
                          onChange={(event) =>
                            updateEditorState((current) => ({
                              ...current,
                              startDate: event.target.value,
                            }))
                          }
                        />
                        <Input
                          type="datetime-local"
                          value={editorState.endDate}
                          onChange={(event) =>
                            updateEditorState((current) => ({
                              ...current,
                              endDate: event.target.value,
                            }))
                          }
                        />
                      </div>
                    ) : (
                      <span className="text-sm font-medium leading-6 text-foreground">
                        {scheduleSummary}
                      </span>
                    )}
                  </InlineCampaignDetailRow>
                  <CampaignDetailRow
                    icon={FileText}
                    label={intl.formatMessage({
                      id: "campaignDetail.contentCadenceTitle",
                      defaultMessage: "Content per week",
                    })}
                    value={intl.formatMessage(
                      {
                        id: "campaignDetail.contentCadenceValue",
                        defaultMessage:
                          "{count, plural, one {# planned post across this campaign} other {# planned posts across this campaign}}",
                      },
                      { count: plannedPosts.length },
                    )}
                    compact={true}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="rounded-3xl border border-border/70 bg-card p-5 text-sm text-muted-foreground">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 font-medium text-foreground">
                  <CreditCard className="h-4 w-4 text-foreground/55" aria-hidden="true" />
                  <FormattedMessage id="campaignDetail.runUsageTitle" defaultMessage="Run usage" />
                </div>
                <p className="font-semibold tabular-nums text-foreground">
                  <FormattedMessage
                    id="campaignDetail.runsUsedValue"
                    defaultMessage="{count, plural, one {# run} other {# runs}}"
                    values={{ count: campaign.runUsage?.runsUsed ?? 0 }}
                  />
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
      <CampaignDetailActionBar
        backPath={buildBrandPath(brand.id, "/campaigns")}
        campaignStatus={campaign.status}
        editorReady={Boolean(editorState)}
        hasUnsavedChanges={hasUnsavedEditorChanges}
        savePending={saveEditorPending}
        editorError={editorError}
        previewRegenerationNotice={previewRegenerationNotice}
        approveDisabled={approveDisabled}
        approvePending={approveCampaign.isPending}
        rejectPending={rejectCampaign.isPending}
        onCancel={resetEditorState}
        onSave={() => setPendingSaveConfirmation(true)}
        onApprove={() => setPendingApprovalConfirmation(true)}
        onReject={() => rejectCampaign.mutate({ brandId: brand.id, id: campaign.id })}
      />
      <RunConfirmationDialog
        open={pendingSaveConfirmation}
        title={intl.formatMessage({
          id: "campaignDetail.previewRunConfirmTitle",
          defaultMessage: "Use runs to regenerate the preview?",
        })}
        description={intl.formatMessage({
          id: "campaignDetail.previewRunConfirmDescription",
          defaultMessage: "Cheetah will save your edits and regenerate campaign preview assets.",
        })}
        confirmLabel={intl.formatMessage({
          id: "campaignDetail.previewRunConfirmAction",
          defaultMessage: "Confirm",
        })}
        quote={previewRegenerationQuoteQuery.data ?? null}
        quotePending={
          previewRegenerationQuoteQuery.isPending || previewRegenerationQuoteQuery.isFetching
        }
        confirmPending={saveEditorPending}
        error={previewRegenerationQuoteQuery.error?.message ?? editorError}
        onCancel={() => {
          if (!saveEditorPending) {
            setPendingSaveConfirmation(false);
          }
        }}
        upgradeLabel={assetCreationGate.upgradeLabel}
        onUpgrade={assetCreationGate.openUpgradeModal}
        onConfirm={() => void saveCampaignEditor()}
      />
      <RunConfirmationDialog
        open={pendingApprovalConfirmation}
        title={intl.formatMessage({
          id: "campaignDetail.approveRunConfirmTitle",
          defaultMessage: "Use runs to approve this campaign?",
        })}
        description={intl.formatMessage({
          id: "campaignDetail.approveRunConfirmDescription",
          defaultMessage:
            "Cheetah will queue generation for campaign posts that still need assets.",
        })}
        confirmLabel={intl.formatMessage({
          id: "campaignDetail.approveRunConfirmAction",
          defaultMessage: "Approve and use runs",
        })}
        quote={approvalQuoteQuery.data ?? null}
        quotePending={approvalQuoteQuery.isPending || approvalQuoteQuery.isFetching}
        confirmPending={approveCampaign.isPending}
        error={approvalQuoteQuery.error?.message ?? approveCampaign.error?.message ?? null}
        onCancel={() => {
          if (!approveCampaign.isPending) {
            setPendingApprovalConfirmation(false);
          }
        }}
        upgradeLabel={assetCreationGate.upgradeLabel}
        onUpgrade={assetCreationGate.openUpgradeModal}
        onConfirm={confirmCampaignApproval}
      />
      <RunConfirmationDialog
        open={pendingPreviewRegenerationConfirmation}
        title={intl.formatMessage({
          id: "campaignDetail.regeneratePreviewConfirmTitle",
          defaultMessage: "Regenerate campaign preview?",
        })}
        description={intl.formatMessage({
          id: "campaignDetail.regeneratePreviewConfirmDescription",
          defaultMessage:
            "Cheetah will queue fresh preview assets for the campaign post in review.",
        })}
        confirmLabel={intl.formatMessage({
          id: "campaignDetail.regeneratePreviewConfirmAction",
          defaultMessage: "Regenerate and use runs",
        })}
        quote={previewRegenerationQuoteQuery.data ?? null}
        quotePending={
          previewRegenerationQuoteQuery.isPending || previewRegenerationQuoteQuery.isFetching
        }
        confirmPending={regeneratePreview.isPending}
        error={
          previewRegenerationQuoteQuery.error?.message ?? regeneratePreview.error?.message ?? null
        }
        onCancel={() => {
          if (!regeneratePreview.isPending) {
            setPendingPreviewRegenerationConfirmation(false);
          }
        }}
        upgradeLabel={assetCreationGate.upgradeLabel}
        onUpgrade={assetCreationGate.openUpgradeModal}
        onConfirm={confirmPreviewRegeneration}
      />
      <RunConfirmationDialog
        open={Boolean(pendingRetryScheduledPostId)}
        title={intl.formatMessage({
          id: "campaignDetail.retryRunConfirmTitle",
          defaultMessage: "Use runs to retry this post?",
        })}
        description={intl.formatMessage({
          id: "campaignDetail.retryRunConfirmDescription",
          defaultMessage: "Cheetah will queue a new generation attempt for this campaign post.",
        })}
        confirmLabel={intl.formatMessage({
          id: "campaignDetail.retryRunConfirmAction",
          defaultMessage: "Retry and use runs",
        })}
        quote={retryGenerationQuoteQuery.data ?? null}
        quotePending={retryGenerationQuoteQuery.isPending || retryGenerationQuoteQuery.isFetching}
        confirmPending={retryScheduledPostGeneration.isPending}
        error={
          retryGenerationQuoteQuery.error?.message ??
          retryScheduledPostGeneration.error?.message ??
          null
        }
        onCancel={() => {
          if (!retryScheduledPostGeneration.isPending) {
            setPendingRetryScheduledPostId(null);
          }
        }}
        upgradeLabel={assetCreationGate.upgradeLabel}
        onUpgrade={assetCreationGate.openUpgradeModal}
        onConfirm={confirmRetryScheduledPostGeneration}
      />
      {assetCreationGate.upgradeModal}
    </div>
  );
}

function CampaignDetailActionBar({
  backPath,
  campaignStatus,
  editorReady,
  hasUnsavedChanges,
  savePending,
  editorError,
  previewRegenerationNotice,
  approveDisabled,
  approvePending,
  rejectPending,
  onCancel,
  onSave,
  onApprove,
  onReject,
}: {
  backPath: string;
  campaignStatus: string;
  editorReady: boolean;
  hasUnsavedChanges: boolean;
  savePending: boolean;
  editorError: string | null;
  previewRegenerationNotice: boolean;
  approveDisabled: boolean;
  approvePending: boolean;
  rejectPending: boolean;
  onCancel: () => void;
  onSave: () => void;
  onApprove: () => void;
  onReject: () => void;
}) {
  const isPendingReview = campaignStatus === "pending_review";
  const saveDisabled = !editorReady || !hasUnsavedChanges || savePending;

  return (
    <div className="fixed bottom-0 left-60 right-0 z-40 border-t border-border/70 bg-background/95 px-5 py-3 shadow-[0_-18px_45px_rgba(44,44,42,0.08)] backdrop-blur lg:px-10">
      <div className="mx-auto flex w-full max-w-[92rem] flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 space-y-1">
          <Button asChild variant="ghost" size="sm" className="-ml-2 rounded-xl">
            <Link to={backPath}>
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              <FormattedMessage id="campaignDetail.back" defaultMessage="Campaigns" />
            </Link>
          </Button>
          <p className="text-xs leading-5 text-muted-foreground">
            {editorError ? (
              <span className="text-destructive">{editorError}</span>
            ) : hasUnsavedChanges ? (
              <FormattedMessage
                id="campaignDetail.unsavedChangesHint"
                defaultMessage="Save changes before approving this campaign."
              />
            ) : previewRegenerationNotice ? (
              <FormattedMessage
                id="campaignDetail.previewRegenerationNotice"
                defaultMessage="Edits saved. Preview assets are regenerating for review."
              />
            ) : (
              <FormattedMessage
                id="campaignDetail.savedChangesHint"
                defaultMessage="All campaign edits are saved."
              />
            )}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            onClick={onCancel}
            disabled={!hasUnsavedChanges || savePending}
          >
            <FormattedMessage id="campaignDetail.cancelInlineEdits" defaultMessage="Cancel" />
          </Button>
          <Button type="button" className="rounded-xl" onClick={onSave} disabled={saveDisabled}>
            {savePending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="h-4 w-4" aria-hidden="true" />
            )}
            <FormattedMessage id="campaignDetail.saveInlineEdits" defaultMessage="Save Changes" />
          </Button>
          {isPendingReview ? (
            <>
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                onClick={onReject}
                disabled={rejectPending || savePending}
              >
                {rejectPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <X className="h-4 w-4" aria-hidden="true" />
                )}
                <FormattedMessage
                  id="campaignDetail.rejectCampaign"
                  defaultMessage="Reject Campaign"
                />
              </Button>
              <Button
                type="button"
                className="rounded-xl"
                onClick={onApprove}
                disabled={approveDisabled}
              >
                {approvePending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Check className="h-4 w-4" aria-hidden="true" />
                )}
                <FormattedMessage
                  id="campaignDetail.approveCampaign"
                  defaultMessage="Approve Campaign"
                />
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function CampaignDetailRow({
  icon: Icon,
  label,
  value,
  compact = false,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        "grid gap-3 py-4",
        compact ? "grid-cols-1" : "sm:grid-cols-[10rem_minmax(0,1fr)] sm:items-center",
      )}
    >
      <div className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
        <Icon className="h-4 w-4" aria-hidden="true" />
        <span>{label}</span>
      </div>
      <div className="text-sm font-medium leading-6 text-foreground">{value}</div>
    </div>
  );
}

function InlineCampaignDetailRow({
  icon: Icon,
  label,
  editing,
  onEdit,
  children,
  compact = false,
}: {
  icon: LucideIcon;
  label: string;
  editing: boolean;
  onEdit: () => void;
  children: React.ReactNode;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        "grid gap-4 py-4",
        compact ? "grid-cols-1" : "sm:grid-cols-[14rem_minmax(0,1fr)]",
      )}
    >
      <div className="flex min-w-0 items-start gap-2 pt-1 text-sm font-medium text-muted-foreground">
        <Icon className="mt-0.5 h-4 w-4" aria-hidden="true" />
        <span>{label}</span>
      </div>
      {editing ? (
        <div>{children}</div>
      ) : (
        <button
          type="button"
          className="group flex w-full items-start justify-between gap-3 rounded-xl text-left outline-none transition hover:bg-muted/45 focus-visible:ring-2 focus-visible:ring-ring"
          onClick={onEdit}
        >
          <div className="min-w-0">{children}</div>
          <span className="mt-0.5 inline-flex shrink-0 text-muted-foreground transition group-hover:text-foreground">
            <PencilLine className="h-4 w-4" aria-hidden="true" />
          </span>
        </button>
      )}
    </div>
  );
}

function CampaignQueuedPostCard({
  post,
  campaignStatus,
  profileLabel,
  logoAssets,
  timeLabel,
  onRetryGeneration,
  retryGenerationPending,
}: {
  post: CampaignSlotCardData;
  campaignStatus: string;
  profileLabel: string;
  logoAssets?: ScheduledPostPreviewLogoAsset[];
  timeLabel: string;
  onRetryGeneration: () => void;
  retryGenerationPending: boolean;
}) {
  const intl = useIntl();
  const preview = resolveScheduledPostPreview({
    output: post.output,
    draft: post.draft ?? null,
    logoAssets,
  });
  const isFailed = post.status === "failed" || post.output.latestAttempt?.status === "failed";
  const productionUnlocked = isCampaignProductionUnlocked(campaignStatus);
  const showGeneratingSkeleton =
    (productionUnlocked || post.isPreview === true) && isPostGenerationInFlight(post) && !isFailed;
  const displayStatus = isFailed ? "failed" : showGeneratingSkeleton ? "generating" : "scheduled";
  const statusLabel = isFailed
    ? intl.formatMessage({
        id: "campaignDetail.plannedStatus.failed",
        defaultMessage: getPlannedPostStatusLabel("failed"),
      })
    : intl.formatMessage({
        id: `campaignDetail.plannedStatus.${displayStatus}`,
        defaultMessage: getPlannedPostStatusLabel(displayStatus),
      });
  const captionFallback = intl.formatMessage({
    id: "campaignDetail.previewCaptionPending",
    defaultMessage: "Caption will appear here once generated.",
  });
  const emptyVisualTitle = isFailed
    ? intl.formatMessage({
        id: "campaignDetail.queuedPostFailedTitle",
        defaultMessage: "Generation needs attention",
      })
    : showGeneratingSkeleton
      ? intl.formatMessage({
          id: "campaignDetail.queuedVisualGeneratingTitle",
          defaultMessage: "Visual asset generating",
        })
      : intl.formatMessage({
          id: "campaignDetail.queuedVisualQueuedTitle",
          defaultMessage: "Visual asset queued",
        });
  const emptyVisualDescription = isFailed
    ? (post.output.latestAttempt?.lastError ??
      intl.formatMessage({
        id: "campaignDetail.queuedPostFailedDescription",
        defaultMessage: "Retry generation when ready.",
      }))
    : showGeneratingSkeleton
      ? intl.formatMessage({
          id: "campaignDetail.queuedVisualGeneratingDescription",
          defaultMessage: "We'll attach the generated visual as soon as it is ready.",
        })
      : intl.formatMessage({
          id: "campaignDetail.queuedVisualQueuedDescription",
          defaultMessage: "We'll create the visual once this campaign enters production.",
        });

  return (
    <div
      className={cn(
        "flex min-h-[28rem] flex-col justify-between overflow-hidden rounded-[18px] border border-dashed bg-card/55",
        isFailed ? "border-destructive/35 bg-destructive/5" : "border-border",
      )}
    >
      <div>
        <SocialPostPreviewCard
          preview={preview}
          timeLabel={timeLabel}
          profileLabel={profileLabel}
          approvalLabel={statusLabel}
          showApprovalBadge={true}
          className="rounded-none border-0 shadow-none"
          captionFallback={captionFallback}
          emptyVisualTitle={emptyVisualTitle}
          emptyVisualDescription={emptyVisualDescription}
          emptyVisual={showGeneratingSkeleton ? <GeneratedAssetVisualSkeleton /> : undefined}
        />
      </div>
      {isFailed ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="m-5 rounded-xl"
          onClick={onRetryGeneration}
          disabled={retryGenerationPending}
        >
          {retryGenerationPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Sparkles className="h-4 w-4" aria-hidden="true" />
          )}
          <FormattedMessage id="campaignDetail.retryGeneration" defaultMessage="Retry generation" />
        </Button>
      ) : null}
    </div>
  );
}

function isPostGenerationInFlight(post: CampaignSlotCardData): boolean {
  const latestAttemptStatus = post.output.latestAttempt?.status ?? null;
  return (
    post.status === "generating" ||
    latestAttemptStatus === "queued" ||
    latestAttemptStatus === "processing"
  );
}

function GeneratedAssetVisualSkeleton() {
  return <GeneratedPostVisualSpinner />;
}

function isCampaignProductionUnlocked(status: string) {
  return (
    status === "approved" || status === "active" || status === "paused" || status === "completed"
  );
}

function getGeneratedContentTabs(
  counts: ReturnType<typeof getCampaignGeneratedContentCounts>,
  intl: ReturnType<typeof useIntl>,
): Array<{ id: GeneratedContentFilter; label: string; count: number }> {
  return [
    {
      id: "all",
      label: intl.formatMessage({
        id: "campaignDetail.generatedContentTab.all",
        defaultMessage: "All",
      }),
      count: counts.all,
    },
    {
      id: "pending",
      label: intl.formatMessage({
        id: "campaignDetail.generatedContentTab.pending",
        defaultMessage: "Pending",
      }),
      count: counts.pending,
    },
    {
      id: "approved",
      label: intl.formatMessage({
        id: "campaignDetail.generatedContentTab.approved",
        defaultMessage: "Approved",
      }),
      count: counts.approved,
    },
    {
      id: "denied",
      label: intl.formatMessage({
        id: "campaignDetail.generatedContentTab.denied",
        defaultMessage: "Denied",
      }),
      count: counts.denied,
    },
  ];
}

function getPlannedPostStatusLabel(status: string): string {
  switch (status) {
    case "failed":
      return "Needs attention";
    case "generating":
      return "Generating";
    case "scheduled":
      return "Queued";
    default:
      return status;
  }
}

function getDefaultCampaignStatusLabel(status: string): string {
  switch (status) {
    case "draft":
      return "Draft";
    case "pending_review":
      return "Pending Review";
    case "approved":
      return "Approved";
    case "active":
      return "Active";
    case "paused":
      return "Paused";
    case "completed":
      return "Completed";
    default:
      return status;
  }
}
